import * as React from 'react';
import { buildClassString } from '../StringUtils';
import { Tooltip } from '../Tooltip';
import './style/index.scss';
export class BarChart extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = Object.assign({}, {
            bars: this._getBars(props)
        });
    }
    static computeDataRange(bars) {
        const chartRange = {
            min: 0,
            max: 0
        };
        if (!bars) {
            return chartRange;
        }
        const margin = 0.05;
        const minVal = bars.reduce((acc, bar) => Math.min(acc, bar.value), 0);
        const maxVal = bars.reduce((acc, bar) => Math.max(acc, bar.value), 0);
        const hasBarNegativeLabels = bars.some(bar => !!bar.label && bar.value < 0);
        const bottomMargin = margin + (hasBarNegativeLabels ? margin * 2 : 0);
        const hasBarPositiveLabels = bars.some(bar => !!bar.label && bar.value >= 0);
        const topMargin = margin + (hasBarPositiveLabels ? margin * 2 : 0);
        const range = maxVal - minVal;
        const expandedRange = range / (1.0 - topMargin - bottomMargin);
        chartRange.min = minVal - (expandedRange * bottomMargin);
        chartRange.max = maxVal + expandedRange * topMargin;
        return chartRange;
    }
    static countDecimalPlaces(val) {
        val = Math.abs(val - Math.trunc(val));
        if (val === 0) {
            return 0;
        }
        val += val * .001;
        let places = 0;
        while (Math.floor(val * 10) === 0) {
            val *= 10;
            ++places;
        }
        while (Math.floor(val * 10) % 10 !== 0) {
            val *= 10;
            ++places;
        }
        return places;
    }
    static computeQuantAxisMarkerVals(chartRange) {
        const possibleIncrements = [1, 2, 2.5, 5];
        const maxMarkerCount = 5;
        let increment;
        let markerMax;
        let magnitude = Math.pow(10, Math.floor(Math.log10(chartRange.max - chartRange.min)) - 1);
        let markerCount = maxMarkerCount + 1;
        while (markerCount > maxMarkerCount) {
            for (const possibleIncrement of possibleIncrements) {
                increment = possibleIncrement * magnitude;
                markerMax = Math.trunc(chartRange.max / increment + 0.66) * increment;
                const markerMin = Math.trunc(chartRange.min / increment - 0.66) * increment;
                markerCount = (markerMax - markerMin) / increment + 1;
                if (markerCount <= maxMarkerCount) {
                    break;
                }
            }
            magnitude *= 10;
        }
        const markerVals = [];
        for (let i = 0; i < markerCount; i++) {
            markerVals.push(markerMax - (i * increment));
        }
        return markerVals;
    }
    componentWillReceiveProps(nextProps) {
        this.setState({ bars: this._getBars(nextProps) });
    }
    render() {
        const { showGridLines, showQuantAxis, verticalBarChart } = this.props;
        const { bars } = this.state;
        if (!bars || bars.length === 0) {
            return null;
        }
        const dataRange = BarChart.computeDataRange(bars);
        const quantAxisMarkers = (showGridLines || showQuantAxis) ?
            BarChart.computeQuantAxisMarkerVals(dataRange) :
            null;
        const chartRange = quantAxisMarkers ?
            {
                min: Math.min(dataRange.min, quantAxisMarkers[quantAxisMarkers.length - 1]),
                max: Math.max(dataRange.max, quantAxisMarkers[0])
            } :
            dataRange;
        return (React.createElement("div", { className: "barchart" },
            React.createElement("div", { className: "barchart__horiz-container" },
                this._renderQuantAxis(chartRange, quantAxisMarkers),
                React.createElement("div", { className: "barchart__chart-body" },
                    this._renderGridLines(chartRange, quantAxisMarkers),
                    verticalBarChart ? this._renderVerticalBar() : this._renderBars(chartRange))),
            this._renderCatAxis(quantAxisMarkers)));
    }
    static _getAxisIncrementDigits(quantAxisMarkers) {
        const increment = quantAxisMarkers.length >= 2 ?
            quantAxisMarkers[0] - quantAxisMarkers[1] :
            0;
        return BarChart.countDecimalPlaces(increment);
    }
    _getBars(props) {
        const { bars, minBars, minBarLoc } = props;
        const emptyBars = [];
        if (minBars && bars.length < minBars) {
            for (let i = 0; i < minBars - bars.length; i++) {
                emptyBars.push({ value: 1, empty: true });
            }
        }
        const combinedBars = (minBarLoc || 'left') === 'left' ? emptyBars.concat(bars) : bars.concat(emptyBars);
        return combinedBars;
    }
    _formatQuantAxisValue(value, fractionalDigits) {
        const { quantAxisFormatFunc } = this.props;
        const defaultFormatFunc = (val) => val.toLocaleString(undefined, {
            maximumFractionDigits: Math.max(0, fractionalDigits)
        });
        return quantAxisFormatFunc ?
            quantAxisFormatFunc(value, defaultFormatFunc) :
            defaultFormatFunc(value);
    }
    _renderQuantAxis(chartRange, quantAxisMarkers) {
        const { showQuantAxis } = this.props;
        if (!showQuantAxis) {
            return null;
        }
        const fractionalDigits = BarChart._getAxisIncrementDigits(quantAxisMarkers);
        const rangeSpan = chartRange.max - chartRange.min;
        const axisLabels = quantAxisMarkers.map(val => this._formatQuantAxisValue(val, fractionalDigits));
        return (React.createElement("div", { className: "barchart__quant-axis" },
            this._renderQuantAxisPlaceholder(quantAxisMarkers),
            quantAxisMarkers.map((val, i) => {
                const top = (chartRange.max - val) / rangeSpan * 100;
                const labelClass = buildClassString({
                    'barchart__quant-axis-label': true,
                    'zero': val === 0
                });
                return (React.createElement("div", { className: labelClass, style: { top: `${top}%` }, key: axisLabels[i] }, axisLabels[i]));
            })));
    }
    _renderQuantAxisPlaceholder(quantAxisMarkers) {
        const fractionalDigits = BarChart._getAxisIncrementDigits(quantAxisMarkers);
        const axisLabels = quantAxisMarkers.map(val => this._formatQuantAxisValue(val, fractionalDigits));
        const longestValue = axisLabels.reduce((label, acc) => {
            return label.length > acc.length ? label : acc;
        }, '');
        return React.createElement("div", { className: "barchart__quant-axis-label hidden" }, longestValue);
    }
    _renderGridLines(chartRange, quantAxisMarkers) {
        const { showGridLines } = this.props;
        if (!showGridLines) {
            return null;
        }
        const rangeSpan = chartRange.max - chartRange.min;
        return (React.createElement("div", { className: "barchart__grid-line-container" }, quantAxisMarkers.map(val => {
            const top = (chartRange.max - val) / rangeSpan * 100;
            const gridlineClass = buildClassString({
                'barchart__grid-line': true,
                'zero': val === 0
            });
            return (React.createElement("div", { className: gridlineClass, style: { top: `${top}%` }, key: val }));
        })));
    }
    _renderVerticalBar() {
        const { bars } = this.state;
        const totalRange = bars.reduce((acc, bar) => acc + bar.value, 0);
        const barElts = [];
        bars.forEach((bar, index) => {
            barElts.push(this._renderVerticalBarColumn(index, bar, totalRange));
        });
        return (React.createElement("div", { className: "barchart__bar-container barchart__bar-container--vertical" }, barElts));
    }
    _renderVerticalBarColumn(index, bar, totalRange) {
        if (!bar || bar.value <= 0 || totalRange <= 0) {
            return null;
        }
        const { value, className } = bar;
        const barClassName = buildClassString({
            barchart__bar: true,
            [className]: !!className
        });
        let barHeight = 0;
        if (totalRange) {
            barHeight = (Math.max(0, value) / totalRange) * 100;
        }
        const barStyle = {
            height: `${barHeight}%`
        };
        return (React.createElement("div", { className: barClassName, style: barStyle }));
    }
    _renderBars(chartRange) {
        const { bars } = this.state;
        const barElts = [];
        bars.forEach((bar, index) => {
            barElts.push(React.createElement("div", { className: "barchart__bar-column-padding", key: `${index}-bar-before` }));
            barElts.push(this._renderBarColumn(index, bar, chartRange));
            barElts.push(React.createElement("div", { className: "barchart__bar-column-padding", key: `${index}-bar-after` }));
        });
        return (React.createElement("div", { className: "barchart__bar-container" }, barElts));
    }
    _renderBarColumn(index, bar, chartRange) {
        if (!bar) {
            return null;
        }
        const { showDeltaTooltip } = this.props;
        const { bars } = this.state;
        const { value, label, className } = bar;
        const negative = value < 0;
        const barChunks = this._getBarChunks(index, chartRange);
        let percentHeightAdded = 0;
        let percentTopAdded = 0;
        const barClassName = buildClassString({
            'barchart__bar-column': true,
            [className]: !!className
        });
        const labelClassName = buildClassString({
            'barchart__bar-label': true,
            'negative': negative
        });
        for (const barChunk of barChunks) {
            const percentHeight = barChunk.percentHeight;
            percentHeightAdded = percentHeight ? percentHeightAdded + percentHeight : percentHeightAdded;
            percentTopAdded = barChunk.percentTop;
        }
        const labelTop = negative ? percentTopAdded : percentTopAdded - percentHeightAdded;
        const labelStyle = {
            top: `${labelTop}%`
        };
        const barFragment = (React.createElement(React.Fragment, null,
            barChunks.map((chunk, chunkIndex) => (React.createElement("div", { className: `
                            barchart__bar
                            ${bar.empty ?
                    'barchart__bar--empty' :
                    `barchart__bar-chunk-${barChunks.length - chunkIndex}`}
                            ${chunkIndex === 0 ? 'barchart__bar-chunk-first' : ''}
                            ${chunkIndex === barChunks.length - 1 ? 'barchart__bar-chunk-last' : ''}
                        `, style: { top: `${chunk.percentTop}%`, height: `${chunk.percentHeight}%` }, key: chunkIndex }))),
            label && React.createElement("div", { className: labelClassName, style: labelStyle },
                React.createElement("span", null, label))));
        if (showDeltaTooltip && index > 0 && !bars[index].empty && !bars[index - 1].empty) {
            const pct = ((bars[index].value - bars[index - 1].value) / bars[index - 1].value) * 100;
            const pctChange = `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
            return (React.createElement(Tooltip, { id: `barchart__bar-${index}`, wrapperClassName: barClassName, place: 'left', content: pctChange, key: index }, barFragment));
        }
        return (React.createElement("div", { key: index, className: barClassName }, barFragment));
    }
    _getBarChunks(index, chartRange) {
        const { minBarHeight, firstBarHeightPercent: firstBarHeight } = this.props;
        const { bars } = this.state;
        const totalHeight = 95;
        if (bars[index].empty) {
            const barHeight = minBarHeight || 1;
            return [{ percentHeight: barHeight || 1, percentTop: totalHeight - barHeight }];
        }
        const chunks = [];
        const range = chartRange.max - chartRange.min;
        let curIndex = index;
        const spaceAboveFirstBar = totalHeight - firstBarHeight;
        const funcBarHeight = (bar) => Math.abs(bar.value) / range * 100;
        let barTop = (chartRange.max - Math.max(0, bars[index].value)) / range * 100;
        const max = Math.max.apply(Math, bars.map((bar) => bar.value));
        const totalIncrease = max - bars[0].value;
        if (firstBarHeight) {
            if (firstBarHeight > 99 || firstBarHeight < 1) {
                throw new RangeError('firstBarHeight must be between 1 and 99.');
            }
            barTop = chartRange.max / range * 100 - firstBarHeight;
            if (index > 0 && bars[curIndex].value > bars[0].value) {
                barTop -= (((bars[curIndex].value - bars[0].value) / totalIncrease) * spaceAboveFirstBar);
            }
        }
        while (curIndex >= 0) {
            const bar = bars[curIndex];
            let barHeight = funcBarHeight(bar);
            if (curIndex > 0) {
                if (firstBarHeight) {
                    if (bars[curIndex - 1].value > bars[0].value && bar.value > bars[curIndex - 1].value) {
                        const currentIncrease = bar.value - bars[curIndex - 1].value;
                        barHeight = (currentIncrease / totalIncrease) * spaceAboveFirstBar;
                    }
                    else if (bars[curIndex - 1].value <= bars[0].value && bar.value > bars[curIndex - 1].value) {
                        const currentIncrease = bar.value - bars[0].value;
                        barHeight = (currentIncrease / totalIncrease) * spaceAboveFirstBar;
                    }
                    else if ((bar.value > bars[0].value && bar.value < bars[curIndex - 1].value)) {
                        const currentIncrease = bar.value - bars[0].value;
                        barHeight = (currentIncrease / totalIncrease) * spaceAboveFirstBar + firstBarHeight;
                    }
                    if (index === bars.length - 1 && bars[0].empty && !bars[1].empty) {
                        if (curIndex === bars.length - 1) {
                            barHeight = spaceAboveFirstBar;
                        }
                        else if (curIndex === bars.length - 2) {
                            barTop = 0;
                        }
                    }
                }
                if (bar.value <= bars[curIndex - 1].value || bars[curIndex - 1].empty) {
                    if (firstBarHeight && (bars[curIndex].value <= bars[0].value)) {
                        barHeight = firstBarHeight;
                    }
                    else if (firstBarHeight && bars[curIndex - 1].empty) {
                        barTop = spaceAboveFirstBar;
                        barHeight = firstBarHeight;
                        if (index >= 2 && bars[index - 2].empty) {
                            barTop = 0;
                            if (bars[index - 1].empty) {
                                barHeight = totalHeight;
                            }
                        }
                    }
                    else if (firstBarHeight && curIndex >= 2 && bars[curIndex - 2].empty) {
                        barTop = spaceAboveFirstBar;
                        barHeight = firstBarHeight;
                    }
                    chunks.push({ percentHeight: barHeight, percentTop: barTop });
                    break;
                }
                if (!firstBarHeight) {
                    barHeight -= funcBarHeight(bars[curIndex - 1]);
                }
            }
            if (firstBarHeight && (curIndex === 0 || bars[curIndex - 1].empty)) {
                barHeight = firstBarHeight;
            }
            chunks.push({ percentHeight: barHeight, percentTop: barTop });
            curIndex--;
        }
        if (minBarHeight) {
            const totalBarHeight = chunks.reduce((a, b) => a + (b.percentHeight || 0), 0);
            if (totalBarHeight < minBarHeight) {
                return [{ percentHeight: minBarHeight, percentTop: totalHeight - minBarHeight }];
            }
        }
        return chunks;
    }
    _renderCatAxis(quantAxisMarkers) {
        const { showQuantAxis } = this.props;
        const { bars } = this.state;
        const hasAxisLabels = bars.some((bar) => !!bar.axisLabel);
        if (!hasAxisLabels) {
            return null;
        }
        const axisLabels = bars.map((bar, index) => {
            const { axisLabel, className } = bar;
            const labelClass = buildClassString({
                'barchart__cat-axis-label': true,
                [className]: !!className
            });
            return React.createElement("div", { key: index, className: labelClass }, axisLabel || '');
        });
        return (React.createElement("div", { className: "barchart__horiz-container no-grow" },
            showQuantAxis &&
                React.createElement("div", { className: "barchart__quant-axis-placeholder" }, this._renderQuantAxisPlaceholder(quantAxisMarkers)),
            React.createElement("div", { className: "barchart__cat-axis" }, axisLabels)));
    }
}
export default BarChart;
